import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Bell, BellOff, BellRing, Check, X, Send } from "lucide-react";
import {
  isPushSupported, getPushPermission, subscribeToPush, unsubscribeFromPush,
  getCurrentSubscription,
} from "@/lib/pwa";
import { pushVapidKey, pushSubscribe, pushUnsubscribe, pushGetPrefs, pushSetPrefs, pushTest } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Push preferences card (mounts on /profile) + first-time inline prompt for
 * signed-in users. Users can flip a master switch and toggle any of the
 * event kinds; a "Send me a test push" action verifies plumbing end-to-end.
 */

const KIND_META = [
  { key: "tip", label: "Coin tips", desc: "Someone tipped you or your loop" },
  { key: "comment", label: "Comments", desc: "New comment on your app" },
  { key: "reply", label: "Replies", desc: "Someone replied to your comment" },
  { key: "follow", label: "New followers", desc: "A developer or fan followed you" },
  { key: "streak", label: "Streak rewards", desc: "Daily launch bonus" },
  { key: "admin", label: "Announcements", desc: "Platform updates from Jixxeo" },
  { key: "cashout", label: "Cashouts", desc: "Payout approved, rejected or paid" },
  { key: "app_update", label: "New app drops", desc: "Fresh releases from creators you follow" },
];

const PROMPT_KEY = "jixxeo_push_prompt_shown";

export function PushPreferences() {
  const [supported] = useState(() => isPushSupported());
  const [perm, setPerm] = useState(() => getPushPermission());
  const [enabled, setEnabled] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!supported) return;
    setPerm(getPushPermission());
    const sub = await getCurrentSubscription();
    setEnabled(!!sub);
    try {
      const p = await pushGetPrefs();
      setPrefs(p.preferences);
    } catch {}
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const enable = async () => {
    setBusy(true);
    try {
      if (perm === "denied") {
        toast.error("Notifications blocked", { description: "Open your browser site settings to re-enable." });
        return;
      }
      const { public_key } = await pushVapidKey();
      if (!public_key) throw new Error("Server has no VAPID key");
      const sub = await subscribeToPush(public_key);
      await pushSubscribe(sub.toJSON(), navigator.userAgent);
      setEnabled(true);
      setPerm(getPushPermission());
      toast.success("Push enabled ⚡", { description: "You'll get pinged for the activity you care about." });
    } catch (e) {
      toast.error("Couldn't enable push", { description: String(e.message || e) });
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const endpoint = await unsubscribeFromPush();
      if (endpoint) await pushUnsubscribe(endpoint);
      setEnabled(false);
      toast.success("Push disabled");
    } catch (e) {
      toast.error("Couldn't disable");
    } finally { setBusy(false); }
  };

  const toggleKind = async (key) => {
    if (!prefs) return;
    const nxt = { ...prefs, [key]: !prefs[key] };
    setPrefs(nxt);
    try {
      await pushSetPrefs(nxt);
    } catch {
      setPrefs(prefs);
      toast.error("Couldn't save preference");
    }
  };

  const sendTest = async () => {
    try {
      const r = await pushTest();
      const st = r?.push?.status || r?.push;
      if (st === "sent") toast.success("Test push sent", { description: "Check your notification tray." });
      else if (st === "no_subscription") toast.error("This device isn't registered yet", { description: "Turn push off and on again." });
      else if (st === "skipped_pref") toast.error("Push is off for announcements in your notification settings");
      else toast.error("Push couldn't be delivered", { description: (r?.push?.errors || [])[0] || "Try turning push off and on again." });
    } catch {
      toast.error("Couldn't send test");
    }
  };

  if (!supported) {
    return (
      <div data-testid="push-prefs" className="glass rounded-2xl p-6 border border-white/10">
        <div className="flex items-center gap-3">
          <BellOff className="w-5 h-5 text-white/60" />
          <div>
            <div className="font-heading font-black text-lg">Push notifications</div>
            <div className="text-white/60 text-sm">Your browser doesn't support push. Install Jixxeo to your home screen and open it from there.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="push-prefs" className="glass rounded-2xl p-6 border border-white/10">
      <div className="flex items-start gap-3">
        <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${enabled ? "bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/40" : "bg-white/5 text-white/60 border border-white/10"}`}>
          {enabled ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] mb-1">// push notifications</div>
          <div className="font-heading font-black text-2xl uppercase tracking-tighter">
            {enabled ? "You're plugged in" : "Get pinged instantly"}
          </div>
          <div className="text-white/60 text-sm mt-1">
            {enabled
              ? "Jixxeo will send system notifications for tips, replies, streaks and more — even when this tab is closed."
              : "Enable browser notifications so you never miss a tip, follow, streak reward, or new drop."}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1">
          {enabled ? (
            <button data-testid="push-disable" onClick={disable} disabled={busy}
              className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest border border-white/15 text-white/70 hover:text-white hover:border-white/30 px-3 py-2 rounded-full transition-colors disabled:opacity-50">
              <BellOff className="w-3.5 h-3.5" /> Turn off
            </button>
          ) : (
            <button data-testid="push-enable" onClick={enable} disabled={busy}
              className="inline-flex items-center gap-1.5 bg-[#00F0FF] hover:bg-white text-black font-black mono text-[10px] uppercase tracking-widest px-4 py-2 rounded-full transition-colors disabled:opacity-50">
              <Bell className="w-3.5 h-3.5" /> Enable push
            </button>
          )}
          {enabled && (
            <button data-testid="push-test" onClick={sendTest}
              className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white px-2 py-1">
              <Send className="w-3 h-3" /> Send test
            </button>
          )}
        </div>
      </div>

      {window.location.pathname !== "/settings/notifications" && <a href="/settings/notifications" data-testid="push-open-settings" className="mt-4 inline-block mono text-[10px] uppercase tracking-widest text-[#00F0FF]">Choose which categories send alerts →</a>}
      {false && enabled && prefs && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {KIND_META.map((k) => {
            const on = prefs[k.key] !== false;
            return (
              <button key={k.key}
                data-testid={`push-toggle-${k.key}`}
                onClick={() => toggleKind(k.key)}
                className={`text-left rounded-xl p-3 border transition-colors ${on ? "border-[#00F0FF]/40 bg-[#00F0FF]/[0.06]" : "border-white/10 bg-white/[0.02]"}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-4 h-4 rounded flex items-center justify-center border ${on ? "bg-[#00F0FF] border-[#00F0FF] text-black" : "border-white/25"}`}>
                    {on && <Check className="w-3 h-3" strokeWidth={3} />}
                  </span>
                  <span className="font-heading font-bold text-sm">{k.label}</span>
                </div>
                <div className="mono text-[10px] text-white/50 mt-1 ml-6">{k.desc}</div>
              </button>
            );
          })}
        </div>
      )}
      {perm === "denied" && (
        <div className="mt-4 mono text-[10px] uppercase tracking-widest text-[#FF003C]">
          Blocked at the browser level · open site settings to unblock
        </div>
      )}
    </div>
  );
}

export function PushInlinePrompt() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!isPushSupported()) return;
    if (getPushPermission() === "granted") {
      // keep this device registered for the signed-in member (re-links after login or key rotation)
      pushVapidKey().then(({ public_key }) => public_key && subscribeToPush(public_key))
        .then((sub) => sub && pushSubscribe(sub.toJSON(), navigator.userAgent)).catch(() => {});
      return;
    }
    if (getPushPermission() !== "default") return;
    try {
      const shown = localStorage.getItem(PROMPT_KEY);
      if (shown) return;
    } catch {}
    const t = setTimeout(() => setVisible(true), 6000);
    return () => clearTimeout(t);
  }, [user]);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(PROMPT_KEY, String(Date.now())); } catch {}
  };

  const enable = async () => {
    setBusy(true);
    try {
      const { public_key } = await pushVapidKey();
      const sub = await subscribeToPush(public_key);
      await pushSubscribe(sub.toJSON(), navigator.userAgent);
      toast.success("Push enabled ⚡");
      dismiss();
    } catch (e) {
      toast.error("Couldn't enable push");
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 22, stiffness: 240 }}
          className="fixed bottom-3 inset-x-3 sm:inset-x-auto sm:right-6 sm:bottom-24 z-[65] max-w-sm"
          data-testid="push-inline-prompt"
        >
          <div className="relative glass-strong rounded-2xl p-4 pr-10 border border-[#FFE300]/30 shadow-[0_10px_40px_rgba(255,227,0,0.2)]">
            <button onClick={dismiss} className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white">
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-xl bg-[#FFE300] flex items-center justify-center shadow-[0_0_20px_rgba(255,227,0,0.4)] shrink-0">
                <BellRing className="w-5 h-5 text-black" strokeWidth={2.5} />
              </span>
              <div className="min-w-0">
                <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#FFE300] mb-0.5">// stay in the loop</div>
                <div className="font-heading font-bold text-base leading-tight">Enable push notifications</div>
                <div className="mono text-[11px] text-white/70 mt-1">Tips, replies and daily streaks — straight to your device.</div>
                <div className="mt-3 flex items-center gap-2">
                  <button onClick={enable} disabled={busy} data-testid="push-inline-enable"
                    className="inline-flex items-center gap-1.5 bg-[#FFE300] hover:bg-white text-black font-black mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-full transition-colors disabled:opacity-50">
                    <Bell className="w-3.5 h-3.5" /> Enable
                  </button>
                  <button onClick={dismiss} className="mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white px-2 py-2">
                    Not now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
